import { AccountStates, WorkflowObjectTypes } from '@generated/graphql';
import type { WorkflowDataType } from '@ovComponents/4-module/workflowCompletion/workflowTypes';

type ReviewAccountQueryParams = {
  workflowData: Pick<WorkflowDataType, 'currentAccountId' | 'createdAccounts'>;
  objectType?: WorkflowObjectTypes;
  contextAware: boolean;
  userId?: string;
};

type ReviewAccountQuery = {
  accountIds: string[];
  states: AccountStates[];
  shouldFetch: boolean;
  isWaitingForAccount: boolean;
};

const FALLBACK_OBJECT_TYPES: ReadonlyArray<WorkflowObjectTypes | undefined> = [
  WorkflowObjectTypes.Account,
  WorkflowObjectTypes.Goal,
  WorkflowObjectTypes.SubAccount,
];

// Allow-list: USER, HOUSEHOLD, ACCOUNT_GROUP and callers that provide no
// object type (hard-coded wizards) keep today's behaviour.
const shouldApplyFallback = ({
  contextAware,
  objectType,
}: Pick<ReviewAccountQueryParams, 'contextAware' | 'objectType'>) =>
  contextAware && FALLBACK_OBJECT_TYPES.includes(objectType);

const ONLY_INITIATED = [AccountStates.Initiated];

// An account the workflow did not create is already past INITIATED.
const SEEDED_ACCOUNT_STATES = [
  AccountStates.Active,
  AccountStates.Requested,
  AccountStates.Frozen,
  AccountStates.Initiated,
  AccountStates.Ready,
];

// Without the flag this is today's rule: only accounts the workflow created,
// and nothing until `currentAccountId` is set. With the flag, a missing
// `createdAccounts` is seeded from `currentAccountId` (ACCOUNT workflows) and
// a workflow with neither key stops waiting instead of showing a loader. The
// seeded fetch also needs a resolved user id: an unowned completion would
// otherwise query accounts with no user filter.
export const resolveReviewAccountQuery = ({
  workflowData,
  objectType,
  contextAware,
  userId,
}: ReviewAccountQueryParams): ReviewAccountQuery => {
  const { currentAccountId } = workflowData;
  const createdAccounts = workflowData.createdAccounts ?? [];
  const createdAccountIds = createdAccounts.flatMap(({ id }) =>
    id ? [id] : [],
  );

  if (
    shouldApplyFallback({ contextAware, objectType }) &&
    createdAccounts.length === 0
  ) {
    return {
      accountIds: currentAccountId ? [currentAccountId] : [],
      states: SEEDED_ACCOUNT_STATES,
      shouldFetch: !!currentAccountId && !!userId,
      isWaitingForAccount: false,
    };
  }

  return {
    accountIds: createdAccountIds,
    states: ONLY_INITIATED,
    shouldFetch: !!currentAccountId && createdAccounts.length > 0,
    isWaitingForAccount: !currentAccountId,
  };
};
